// File: frontend/src/pages/AttendancePage.jsx

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import toast from 'react-hot-toast';
import Spinner from '../components/Spinner';
import { useAuth } from '../AuthContext';
import { classesAPI, attendanceAPI, feesAPI } from '../services/api';
import AttendanceTable from '../components/AttendanceTable';
import TrialStudentModal from '../components/TrialStudentModal';
import { useLanguage } from '../LanguageContext';
import classNames from 'classnames';

// --- Date Math Helpers (Avoid timezone offset shifts) ---
const parseYMD = (str) => {
    if (!str) return new Date();
    const [y, m, d] = str.split('-').map(Number);
    return new Date(y, m - 1, d);
};

const formatYMD = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const getTodayString = () => formatYMD(new Date());

const getMonday = (ymdStr) => {
    const d = parseYMD(ymdStr);
    const day = d.getDay(); // 0 is Sun, 1 is Mon, ... 6 is Sat
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(d.getFullYear(), d.getMonth(), diff);
    return formatYMD(monday);
};

const addDays = (ymdStr, days) => {
    const d = parseYMD(ymdStr);
    d.setDate(d.getDate() + days);
    return formatYMD(d);
};

export default function AttendancePage() {
    const { socket } = useAuth();
    const { t, language } = useLanguage();

    // View Mode: 'day' (default), 'week', or 'class'
    const [viewMode, setViewMode] = useState('day');

    // Daily View State
    const [selectedDate, setSelectedDate] = useState(getTodayString());
    const [dayData, setDayData] = useState({ date: getTodayString(), day_name: '', classes: [] });
    const [isLoadingDay, setIsLoadingDay] = useState(false);

    // Weekly View State
    const [currentWeekMonday, setCurrentWeekMonday] = useState(getMonday(getTodayString()));
    const [weekData, setWeekData] = useState([]);
    const [isLoadingWeek, setIsLoadingWeek] = useState(false);
    const [selectedWeekDay, setSelectedWeekDay] = useState(null); // null means "show all days"

    // Single Class View State
    const [allClasses, setAllClasses] = useState([]);
    const [selectedClassId, setSelectedClassId] = useState('');
    const [singleClassData, setSingleClassData] = useState([]);
    const [isLoadingClass, setIsLoadingClass] = useState(false);

    // Common & Modal State
    const [showOnlyTrials, setShowOnlyTrials] = useState(false);
    const [studentForTrialModal, setStudentForTrialModal] = useState(null);
    const [highlightedStudent, setHighlightedStudent] = useState(null);
    const [dirtyClassIds, setDirtyClassIds] = useState(new Set()); // Track classes with unsaved edits

    // --- 1. Load All Classes for Dropdown (and metadata) ---
    useEffect(() => {
        classesAPI.getAll()
            // Paused classes are hidden from roll-call entirely -- they can
            // still be reviewed via Daily/Weekly View on a date that already
            // has recorded attendance.
            .then(data => setAllClasses(data.filter(c => c.is_active !== false)))
            .catch(err => console.error("Failed to fetch classes list:", err));
    }, []);

    // --- 2. Fetch Data for Daily View ---
    const fetchDayData = useCallback(async (dateToFetch) => {
        setIsLoadingDay(true);
        try {
            const data = await attendanceAPI.getByDate({ date: dateToFetch });
            setDayData(data);
            setDirtyClassIds(new Set());
        } catch (err) {
            console.error("Failed to load daily attendance:", err);
            toast.error('Could not load classes for this date.');
        } finally {
            setIsLoadingDay(false);
        }
    }, []);

    // --- 3. Fetch Data for Weekly View ---
    const fetchWeekData = useCallback(async (mondayStr) => {
        setIsLoadingWeek(true);
        try {
            const sundayStr = addDays(mondayStr, 6);
            const data = await attendanceAPI.getByDate({ from_date: mondayStr, to_date: sundayStr });
            setWeekData(data);
            setDirtyClassIds(new Set());
        } catch (err) {
            console.error("Failed to load weekly attendance:", err);
            toast.error('Could not load weekly attendance schedule.');
        } finally {
            setIsLoadingWeek(false);
        }
    }, []);

    // --- 4. Fetch Data for Single Class View ---
    const fetchSingleClassData = useCallback(async () => {
        if (!selectedClassId || !selectedDate) {
            setSingleClassData([]);
            return;
        }
        setIsLoadingClass(true);
        try {
            const data = await attendanceAPI.get(selectedClassId, selectedDate);
            setSingleClassData(data);
            setDirtyClassIds(prev => {
                const next = new Set(prev);
                next.delete(parseInt(selectedClassId));
                return next;
            });
        } catch (err) {
            console.error("Failed to load single class attendance:", err);
            toast.error('Could not load class attendance.');
        } finally {
            setIsLoadingClass(false);
        }
    }, [selectedClassId, selectedDate]);

    // Trigger data fetch on view mode / date changes
    useEffect(() => {
        if (viewMode === 'day') {
            fetchDayData(selectedDate);
        } else if (viewMode === 'week') {
            fetchWeekData(currentWeekMonday);
        } else if (viewMode === 'class') {
            fetchSingleClassData();
        }
    }, [viewMode, selectedDate, currentWeekMonday, fetchDayData, fetchWeekData, fetchSingleClassData]);

    // --- Socket Listeners for Real-Time Updates ---
    useEffect(() => {
        if (!socket) return;

        const handleAttendanceUpdate = (payload) => {
            setHighlightedStudent(payload.studentId);
            setTimeout(() => setHighlightedStudent(null), 1500);

            // Update in day view
            setDayData(prev => ({
                ...prev,
                classes: prev.classes.map(cls => {
                    if (cls.id === payload.classId) {
                        return {
                            ...cls,
                            students: cls.students.map(s => 
                                s.student_id === payload.studentId ? { ...s, status: payload.status } : s
                            )
                        };
                    }
                    return cls;
                })
            }));

            // Update in week view
            setWeekData(prev => prev.map(day => ({
                ...day,
                classes: day.classes.map(cls => {
                    if (cls.id === payload.classId) {
                        return {
                            ...cls,
                            students: cls.students.map(s => 
                                s.student_id === payload.studentId ? { ...s, status: payload.status } : s
                            )
                        };
                    }
                    return cls;
                })
            })));

            // Update in single class view
            setSingleClassData(prev => prev.map(s => 
                s.student_id === payload.studentId ? { ...s, status: payload.status } : s
            ));
        };

        socket.on('attendance_updated', handleAttendanceUpdate);
        return () => {
            socket.off('attendance_updated', handleAttendanceUpdate);
        };
    }, [socket]);

    // --- Student Field Change Handlers ---
    const handleStudentChangeInDay = (classId, studentId, fieldName, value) => {
        setDirtyClassIds(prev => new Set(prev).add(classId));
        setDayData(prev => ({
            ...prev,
            classes: prev.classes.map(cls => {
                if (cls.id === classId) {
                    const updatedStudents = cls.students.map(s => 
                        s.student_id === studentId ? { ...s, [fieldName]: value } : s
                    );
                    const pendingCount = updatedStudents.filter(s => s.status === 'pending').length;
                    return {
                        ...cls,
                        students: updatedStudents,
                        stats: {
                            ...cls.stats,
                            present: updatedStudents.filter(s => s.status === 'present').length,
                            absent: updatedStudents.filter(s => s.status === 'absent').length,
                            late: updatedStudents.filter(s => s.status === 'late').length,
                            pending: pendingCount,
                            is_completed: updatedStudents.length > 0 && pendingCount === 0
                        }
                    };
                }
                return cls;
            })
        }));
    };

    const handleStudentChangeInWeek = (dateStr, classId, studentId, fieldName, value) => {
        setDirtyClassIds(prev => new Set(prev).add(classId));
        setWeekData(prev => prev.map(day => {
            if (day.date === dateStr) {
                return {
                    ...day,
                    classes: day.classes.map(cls => {
                        if (cls.id === classId) {
                            const updatedStudents = cls.students.map(s => 
                                s.student_id === studentId ? { ...s, [fieldName]: value } : s
                            );
                            return {
                                ...cls,
                                students: updatedStudents,
                            };
                        }
                        return cls;
                    })
                };
            }
            return day;
        }));
    };

    const handleStudentChangeInSingleClass = (studentId, fieldName, value) => {
        setDirtyClassIds(prev => new Set(prev).add(parseInt(selectedClassId)));
        setSingleClassData(prev => prev.map(s => 
            s.student_id === studentId ? { ...s, [fieldName]: value } : s
        ));
    };

    // --- Save Class Attendance ---
    const handleSaveSingleClass = async (classId, targetDate, students) => {
        const payload = students.map(({ student_id, status, notes }) => ({ student_id, status, notes }));
        const savePromise = attendanceAPI.recordBulk(classId, payload, targetDate);

        toast.promise(savePromise, {
            loading: 'Saving class attendance...',
            success: () => {
                setDirtyClassIds(prev => {
                    const next = new Set(prev);
                    next.delete(classId);
                    return next;
                });
                // Check trial students
                const attendedTrials = students.filter(s => s.is_trial && (s.status === 'present' || s.status === 'late'));
                if (attendedTrials.length > 0) {
                    setStudentForTrialModal(attendedTrials[0]);
                }
                return <b>Class attendance saved!</b>;
            },
            error: (err) => <b>{err.response?.data?.error || 'Failed to save attendance.'}</b>
        });
    };

    // --- Save All Classes for Day View ---
    const handleSaveAllDayClasses = async () => {
        if (!dayData.classes || dayData.classes.length === 0) return;

        const payload = {
            date: dayData.date,
            classes: dayData.classes.map(cls => ({
                class_id: cls.id,
                attendance_data: cls.students.map(s => ({
                    student_id: s.student_id,
                    status: s.status,
                    notes: s.notes
                }))
            }))
        };

        const savePromise = attendanceAPI.recordBulkClasses(payload);
        toast.promise(savePromise, {
            loading: `Saving attendance for all ${dayData.classes.length} classes...`,
            success: () => {
                setDirtyClassIds(new Set());
                fetchDayData(dayData.date);
                return <b>All {dayData.classes.length} classes saved successfully!</b>;
            },
            error: (err) => <b>{err.response?.data?.error || 'Failed to save all classes.'}</b>
        });
    };

    // --- Cancel Session ---
    const handleCancelSession = async (classId, dateToCancel) => {
        if (window.confirm(`Cancel this class session for ${dateToCancel}? This will remove it from payroll and reset attendance.`)) {
            const cancelPromise = attendanceAPI.cancelSession(classId, dateToCancel);
            toast.promise(cancelPromise, {
                loading: 'Canceling session...',
                success: () => {
                    if (viewMode === 'day') fetchDayData(selectedDate);
                    else if (viewMode === 'week') fetchWeekData(currentWeekMonday);
                    else fetchSingleClassData();
                    return <b>Session canceled successfully.</b>;
                },
                error: (err) => <b>{err.response?.data?.error || 'Could not cancel session.'}</b>
            });
        }
    };

    // --- Cram School Quick Record Session ---
    const handleCramQuickRecord = async (classId, targetDate, status) => {
        try {
            const res = await attendanceAPI.recordCramSession({
                class_id: classId,
                date: targetDate,
                status: status
            });
            toast.success(res.message || (status === 'present' 
                ? (language === 'zh' ? '課堂已記錄為已上課！' : 'Class recorded as held!') 
                : (language === 'zh' ? '課堂已標記為未上課' : 'Class marked as not held')));
            if (viewMode === 'day') fetchDayData(selectedDate);
            else if (viewMode === 'week') fetchWeekData(currentWeekMonday);
            else fetchSingleClassData();
        } catch (err) {
            console.error('Failed to quick record cram session:', err);
            toast.error(err.response?.data?.error || (language === 'zh' ? '記錄課堂狀態失敗' : 'Failed to record session status'));
        }
    };

    // --- Confirm Payment (Bank Transfer / Cash) ---
    const handleConfirmPayment = async (studentIds, amount, name, paymentMethod = 'bank_transfer', classId, feeDate) => {
        const targetClassId = classId || selectedClassId;
        const targetDate = feeDate || selectedDate;
        const isTransfer = paymentMethod === 'bank_transfer';
        const methodLabel = isTransfer ? 'bank transfer' : 'cash';

        const confirmPromise = feesAPI.confirmPayment({
            student_ids: studentIds,
            class_id: targetClassId,
            fee_date: targetDate,
            payment_method: paymentMethod,
            amount: amount,
            notes: `${isTransfer ? 'Bank transfer verified' : 'Cash received'} for ${name} on ${targetDate}`
        });

        toast.promise(confirmPromise, {
            loading: `Confirming ${methodLabel} of $${amount} NTD...`,
            success: () => {
                if (viewMode === 'day') fetchDayData(selectedDate);
                else if (viewMode === 'week') fetchWeekData(currentWeekMonday);
                else fetchSingleClassData();
                return <b>Confirmed $${amount} NTD {methodLabel} for {name}!</b>;
            },
            error: (err) => <b>{err.response?.data?.error || `Could not confirm ${methodLabel}.`}</b>
        });
    };

    // --- Date Navigation Handlers ---
    const handlePrevDay = () => setSelectedDate(prev => addDays(prev, -1));
    const handleNextDay = () => setSelectedDate(prev => addDays(prev, 1));
    const handleToday = () => setSelectedDate(getTodayString());

    const handlePrevWeek = () => setCurrentWeekMonday(prev => addDays(prev, -7));
    const handleNextWeek = () => setCurrentWeekMonday(prev => addDays(prev, 7));
    const handleThisWeek = () => setCurrentWeekMonday(getMonday(getTodayString()));

    // Week days calculation for Weekly view tabs
    const weekDays = useMemo(() => {
        const days = [];
        const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        for (let i = 0; i < 7; i++) {
            const dateStr = addDays(currentWeekMonday, i);
            const foundDay = weekData.find(d => d.date === dateStr);
            days.push({
                dateStr,
                label: dayLabels[i],
                dayName: foundDay?.day_name || dayLabels[i],
                classCount: foundDay?.classes?.length || 0,
                isToday: dateStr === getTodayString()
            });
        }
        return days;
    }, [currentWeekMonday, weekData]);

    const weekSunday = useMemo(() => addDays(currentWeekMonday, 6), [currentWeekMonday]);

    return (
        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 space-y-6">
            {/* Top Header & View Mode Switcher */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b pb-4">
                <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                        <span>📝</span> Class Attendance & Tuition Verification
                    </h2>
                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                        Lookup classes by day or week, one-click mark all present, batch select, and verify bank transfers.
                    </p>
                </div>

                {/* View Mode Tabs */}
                <div className="inline-flex p-1 bg-gray-100 dark:bg-gray-700 rounded-xl self-start md:self-auto border border-gray-200 dark:border-gray-700">
                    <button
                        type="button"
                        onClick={() => setViewMode('day')}
                        className={classNames('px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5', {
                            'bg-white dark:bg-gray-800 text-blue-600 shadow-sm': viewMode === 'day',
                            'text-gray-600 dark:text-gray-400 hover:text-gray-900': viewMode !== 'day'
                        })}
                    >
                        <span>📅</span>
                        <span>{t('att_daily_view')}</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setViewMode('week')}
                        className={classNames('px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5', {
                            'bg-white dark:bg-gray-800 text-blue-600 shadow-sm': viewMode === 'week',
                            'text-gray-600 dark:text-gray-400 hover:text-gray-900': viewMode !== 'week'
                        })}
                    >
                        <span>📆</span>
                        <span>{t('att_weekly_view')}</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setViewMode('class')}
                        className={classNames('px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5', {
                            'bg-white dark:bg-gray-800 text-blue-600 shadow-sm': viewMode === 'class',
                            'text-gray-600 dark:text-gray-400 hover:text-gray-900': viewMode !== 'class'
                        })}
                    >
                        <span>🏫</span>
                        <span>{t('att_single_view')}</span>
                    </button>
                </div>
            </div>

            {/* ========================================================= */}
            {/* VIEW 1: DAILY VIEW (BY DAY) - DEFAULT                      */}
            {/* ========================================================= */}
            {viewMode === 'day' && (
                <div className="space-y-5">
                    {/* Date Navigation Bar */}
                    <div className="flex flex-wrap items-center justify-between gap-3 bg-gray-50/90 p-3 sm:p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-2 flex-wrap">
                            <button
                                type="button"
                                onClick={handlePrevDay}
                                className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 text-gray-700 dark:text-gray-300 text-xs font-bold rounded-lg transition-colors cursor-pointer"
                                title="Previous Day"
                            >
                                ◀ Prev Day
                            </button>
                            <input
                                type="date"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-800 font-bold text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500"
                            />
                            <button
                                type="button"
                                onClick={handleToday}
                                className={classNames('px-3 py-2 text-xs font-bold rounded-lg border transition-colors cursor-pointer', {
                                    'bg-blue-600 text-white border-blue-600': selectedDate === getTodayString(),
                                    'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100': selectedDate !== getTodayString()
                                })}
                            >
                                Today
                            </button>
                            <button
                                type="button"
                                onClick={handleNextDay}
                                className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 text-gray-700 dark:text-gray-300 text-xs font-bold rounded-lg transition-colors cursor-pointer"
                                title="Next Day"
                            >
                                Next Day ▶
                            </button>
                        </div>

                        {/* Summary & Save All Button */}
                        <div className="flex items-center gap-3 flex-wrap">
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                                <span className="font-bold text-gray-900 dark:text-gray-100">{dayData.classes?.length || 0}</span> Classes Scheduled •{' '}
                                <span className="font-bold text-gray-900 dark:text-gray-100">
                                    {dayData.classes?.reduce((sum, c) => sum + (c.students?.length || 0), 0) || 0}
                                </span> Students Total
                            </div>

                            {dayData.classes?.length > 0 && (
                                <button
                                    type="button"
                                    onClick={handleSaveAllDayClasses}
                                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-4 py-2 rounded-xl text-xs shadow-sm transition-colors flex items-center gap-1.5 cursor-pointer"
                                >
                                    <span>💾</span>
                                    <span>Save All ({dayData.classes.length} Classes)</span>
                                    {dirtyClassIds.size > 0 && (
                                        <span className="bg-amber-400 text-amber-950 px-1.5 py-0.2 rounded-full text-[10px] font-extrabold ml-1">
                                            {dirtyClassIds.size} Edited
                                        </span>
                                    )}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Classes Content */}
                    {isLoadingDay ? (
                        <div className="py-16 text-center"><Spinner /></div>
                    ) : !dayData.classes || dayData.classes.length === 0 ? (
                        <div className="py-16 text-center bg-gray-50/60 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700">
                            <p className="text-4xl mb-2">🏖️</p>
                            <h3 className="text-base font-bold text-gray-800 dark:text-gray-200">
                                No classes scheduled for {dayData.day_name || parseYMD(selectedDate).toLocaleDateString('en-US', { weekday: 'long' })} ({selectedDate})
                            </h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-md mx-auto">
                                Classes are scheduled by day of the week. Switch to another day or jump to the Weekly View to see the full schedule.
                            </p>
                            <div className="mt-4 flex justify-center gap-2 flex-wrap">
                                <button
                                    type="button"
                                    onClick={() => setViewMode('week')}
                                    className="px-3.5 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg text-xs font-bold transition-colors cursor-pointer"
                                >
                                    📆 Switch to Weekly View
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {dayData.classes.map((cls) => {
                                const isCram = cls.class_type === 'cram_school';
                                const isDirty = dirtyClassIds.has(cls.id);
                                // cls.hourly_wage is per hour, not per session -- show the real
                                // session total (rate x duration) instead of the bare hourly figure.
                                const cramRate = parseFloat(cls.hourly_wage || cls.rate || 700);
                                const cramDurationHours = (() => {
                                    if (!cls.start_time || !cls.end_time) return 1;
                                    const [sh, sm] = cls.start_time.split(':').map(Number);
                                    const [eh, em] = cls.end_time.split(':').map(Number);
                                    const hours = (eh * 60 + em - (sh * 60 + sm)) / 60;
                                    return hours > 0 ? hours : 1;
                                })();
                                const cramSessionTotal = Math.round(cramRate * cramDurationHours);

                                return (
                                    <div 
                                        key={cls.id} 
                                        className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xs overflow-hidden transition-all hover:border-gray-300"
                                    >
                                        {/* Class Header Banner */}
                                        <div className="bg-gradient-to-r from-gray-50 to-white p-4 border-b border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                            <div>
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <h3 className="text-lg font-extrabold text-gray-900 dark:text-gray-100">{cls.name}</h3>
                                                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
                                                        isCram ? 'bg-purple-100 text-purple-800' : 'bg-emerald-100 text-emerald-800'
                                                    }`}>
                                                        {isCram ? `🏫 Cram School: ${cls.cram_school_name || ''}` : '🏠 Private Tutoring'}
                                                    </span>
                                                    {cls.session_status === 'held' ? (
                                                        isCram ? (
                                                            <span className="bg-emerald-100 text-emerald-900 border border-emerald-300 px-2.5 py-0.5 rounded-full text-xs font-extrabold flex items-center gap-1 shadow-2xs">
                                                                ✓ Class Held (${cramSessionTotal} NTD Secured)
                                                            </span>
                                                        ) : (
                                                            <span className="bg-green-100 text-green-800 px-2.5 py-0.5 rounded-full text-xs font-bold flex items-center gap-1">
                                                                ✓ Attendance Recorded
                                                            </span>
                                                        )
                                                    ) : cls.session_status === 'not_held' ? (
                                                        <span className="bg-red-100 text-red-800 border border-red-200 px-2.5 py-0.5 rounded-full text-xs font-bold flex items-center gap-1">
                                                            ✗ Not Held
                                                        </span>
                                                    ) : (
                                                        <span className="bg-amber-100 text-amber-800 border border-amber-200 px-2.5 py-0.5 rounded-full text-xs font-bold flex items-center gap-1">
                                                            ⏳ {isCram ? 'Pending Check' : `${cls.stats?.pending || cls.students.length} Pending`}
                                                        </span>
                                                    )}
                                                    {isDirty && (
                                                        <span className="bg-amber-500 text-white px-2 py-0.5 rounded-full text-[10px] font-bold">
                                                            ● Unsaved Edits
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                    ⏰ Time: <span className="font-semibold text-gray-700 dark:text-gray-300">{cls.start_time?.slice(0, 5)} - {cls.end_time?.slice(0, 5)}</span>
                                                    {cls.teacher_name && <span> • Teacher: <span className="font-semibold text-gray-700 dark:text-gray-300">{cls.teacher_name}</span></span>}
                                                    {cls.room_name && <span> • Room: <span className="font-semibold text-gray-700 dark:text-gray-300">{cls.room_name}</span></span>}
                                                    <span> • <span className="font-bold text-gray-800 dark:text-gray-200">{cls.students.length}</span> Enrolled Students</span>
                                                </p>
                                            </div>

                                            {/* Action Buttons */}
                                            <div className="flex items-center gap-2 self-start sm:self-auto flex-wrap">
                                                {isCram ? (
                                                    <div className="flex items-center gap-1.5">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleCramQuickRecord(cls.id, selectedDate, 'present')}
                                                            className={classNames('px-3 py-1.5 rounded-lg text-xs font-extrabold shadow-xs transition-colors flex items-center gap-1 cursor-pointer', {
                                                                'bg-emerald-600 text-white ring-2 ring-emerald-300': cls.cram_session_status === 'held',
                                                                'bg-emerald-50 text-emerald-800 border border-emerald-300 hover:bg-emerald-100': cls.cram_session_status !== 'held'
                                                            })}
                                                            title="Record cram school class as held to secure rate"
                                                        >
                                                            <span>✓</span>
                                                            <span>{cls.cram_session_status === 'held' ? t('att_class_held') : t('att_mark_held')}</span>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleCramQuickRecord(cls.id, selectedDate, 'absent')}
                                                            className={classNames('px-2.5 py-1.5 rounded-lg text-xs font-bold transition-colors cursor-pointer flex items-center gap-1', {
                                                                'bg-red-600 text-white': cls.cram_session_status === 'not_held',
                                                                'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:bg-red-50 hover:text-red-700': cls.cram_session_status !== 'not_held'
                                                            })}
                                                            title="Mark class as not held / cancelled"
                                                        >
                                                            <span>✗</span>
                                                            <span>{t('att_mark_not_held')}</span>
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSaveSingleClass(cls.id, selectedDate, cls.students)}
                                                        className={classNames('px-3.5 py-1.5 rounded-lg text-xs font-bold shadow-xs transition-colors flex items-center gap-1.5 cursor-pointer', {
                                                            'bg-emerald-600 hover:bg-emerald-700 text-white': isDirty,
                                                            'bg-blue-600 hover:bg-blue-700 text-white': !isDirty
                                                        })}
                                                    >
                                                        <span>💾</span>
                                                        <span>Save Class</span>
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => handleCancelSession(cls.id, selectedDate)}
                                                    className="px-2.5 py-1.5 text-gray-400 dark:text-gray-500 hover:text-red-600 text-xs rounded-lg hover:bg-red-50 transition-colors cursor-pointer"
                                                    title="Cancel Session for this date"
                                                >
                                                    Cancel Session
                                                </button>
                                            </div>
                                        </div>

                                        {/* Class Students Table */}
                                        <div className="p-4">
                                            <AttendanceTable
                                                attendanceData={cls.students}
                                                classInfo={cls}
                                                currentDate={selectedDate}
                                                onStatusChange={(studentId, field, val) => handleStudentChangeInDay(cls.id, studentId, field, val)}
                                                highlightedStudent={highlightedStudent}
                                                onConfirmPayment={(ids, amt, name, method) => handleConfirmPayment(ids, amt, name, method, cls.id, selectedDate)}
                                                onScheduleMakeup={() => fetchDayData(selectedDate)}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* ========================================================= */}
            {/* VIEW 2: WEEKLY VIEW (BY WEEK)                              */}
            {/* ========================================================= */}
            {viewMode === 'week' && (
                <div className="space-y-5">
                    {/* Week Navigation Header */}
                    <div className="flex flex-wrap items-center justify-between gap-3 bg-gray-50/90 p-3 sm:p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-2 flex-wrap">
                            <button
                                type="button"
                                onClick={handlePrevWeek}
                                className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 text-gray-700 dark:text-gray-300 text-xs font-bold rounded-lg transition-colors cursor-pointer"
                            >
                                ◀ Prev Week
                            </button>
                            <span className="text-sm font-extrabold text-gray-900 dark:text-gray-100 px-1">
                                {currentWeekMonday} ~ {weekSunday}
                            </span>
                            <button
                                type="button"
                                onClick={handleThisWeek}
                                className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 text-gray-700 dark:text-gray-300 text-xs font-bold rounded-lg transition-colors cursor-pointer"
                            >
                                This Week
                            </button>
                            <button
                                type="button"
                                onClick={handleNextWeek}
                                className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 text-gray-700 dark:text-gray-300 text-xs font-bold rounded-lg transition-colors cursor-pointer"
                            >
                                Next Week ▶
                            </button>
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => setSelectedWeekDay(null)}
                                className={classNames('px-3 py-1.5 rounded-lg text-xs font-bold transition-colors cursor-pointer', {
                                    'bg-blue-600 text-white shadow-xs': selectedWeekDay === null,
                                    'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50': selectedWeekDay !== null
                                })}
                            >
                                Show All 7 Days
                            </button>
                        </div>
                    </div>

                    {/* Day Pill Buttons */}
                    <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1">
                        {weekDays.map(d => {
                            const isSelected = selectedWeekDay === d.dateStr;
                            return (
                                <button
                                    key={d.dateStr}
                                    type="button"
                                    onClick={() => setSelectedWeekDay(isSelected ? null : d.dateStr)}
                                    className={classNames('flex-1 min-w-[100px] p-2.5 rounded-xl border text-center transition-all cursor-pointer flex flex-col items-center justify-center', {
                                        'bg-blue-600 text-white border-blue-600 shadow-sm ring-2 ring-blue-300': isSelected,
                                        'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-700 hover:border-blue-400 hover:bg-blue-50/50': !isSelected && !d.isToday,
                                        'bg-blue-50/70 text-blue-950 border-blue-300 font-bold': !isSelected && d.isToday
                                    })}
                                >
                                    <span className="text-[11px] uppercase font-bold tracking-wider opacity-80">
                                        {d.label} {d.isToday && '• Today'}
                                    </span>
                                    <span className="text-xs font-extrabold mt-0.5">
                                        {d.dateStr.slice(5)}
                                    </span>
                                    <span className={classNames('mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold', {
                                        'bg-white/20 text-white': isSelected,
                                        'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300': !isSelected && d.classCount === 0,
                                        'bg-emerald-100 text-emerald-800': !isSelected && d.classCount > 0
                                    })}>
                                        {d.classCount} {d.classCount === 1 ? 'class' : 'classes'}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Week Days List Content */}
                    {isLoadingWeek ? (
                        <div className="py-16 text-center"><Spinner /></div>
                    ) : (
                        <div className="space-y-6">
                            {weekData
                                .filter(day => selectedWeekDay === null || day.date === selectedWeekDay)
                                .map(day => (
                                    <div key={day.date} className="space-y-3">
                                        {/* Day Banner */}
                                        <div className="flex items-center justify-between bg-gray-100/80 px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700">
                                            <div className="flex items-center gap-2">
                                                <span className="font-extrabold text-sm text-gray-900 dark:text-gray-100">
                                                    📅 {day.day_name} ({day.date})
                                                </span>
                                                <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                                                    {day.classes?.length || 0} classes
                                                </span>
                                            </div>
                                            {day.classes?.length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setSelectedDate(day.date);
                                                        setViewMode('day');
                                                    }}
                                                    className="text-xs font-bold text-blue-600 hover:text-blue-800 cursor-pointer"
                                                >
                                                    Open Day View →
                                                </button>
                                            )}
                                        </div>

                                        {/* Classes on that day */}
                                        {!day.classes || day.classes.length === 0 ? (
                                            <p className="text-xs text-gray-400 dark:text-gray-500 italic px-4 py-2">
                                                No classes on this day.
                                            </p>
                                        ) : (
                                            <div className="space-y-4">
                                                {day.classes.map(cls => (
                                                    <div 
                                                        key={`${day.date}-${cls.id}`}
                                                        className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xs overflow-hidden"
                                                    >
                                                        {/* Header */}
                                                        <div className="bg-gray-50 dark:bg-gray-800/60 p-3 border-b border-gray-100 dark:border-gray-700 flex flex-wrap items-center justify-between gap-2">
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <h4 className="text-sm font-bold text-gray-900 dark:text-gray-100">{cls.name}</h4>
                                                                <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                                                                    ⏰ {cls.start_time?.slice(0, 5)} - {cls.end_time?.slice(0, 5)}
                                                                </span>
                                                                <span className={`px-2 py-0.5 rounded text-[11px] font-bold ${
                                                                    cls.class_type === 'cram_school' ? 'bg-purple-100 text-purple-800' : 'bg-emerald-100 text-emerald-800'
                                                                }`}>
                                                                    {cls.class_type === 'cram_school' ? `🏫 Cram: ${cls.cram_school_name || ''}` : '🏠 Private'}
                                                                </span>
                                                                {cls.session_status === 'held' ? (
                                                                    <span className="bg-emerald-100 text-emerald-900 border border-emerald-300 px-2 py-0.5 rounded-full text-[10px] font-extrabold">
                                                                        {cls.class_type === 'cram_school'
                                                                            ? `✓ ${t('att_status_held')} ($${cls.hourly_wage || cls.rate || 700} NTD)`
                                                                            : `✓ ${t('att_status_held')}`}
                                                                    </span>
                                                                ) : cls.session_status === 'not_held' ? (
                                                                    <span className="bg-red-100 text-red-800 border border-red-200 px-2 py-0.5 rounded-full text-[10px] font-bold">
                                                                        ✗ {t('att_status_not_held')}
                                                                    </span>
                                                                ) : (
                                                                    <span className="bg-amber-100 text-amber-800 border border-amber-200 px-2 py-0.5 rounded-full text-[10px] font-bold">
                                                                        ⏳ {t('att_status_pending')}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {cls.class_type === 'cram_school' ? (
                                                                <div className="flex items-center gap-1.5">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleCramQuickRecord(cls.id, day.date, 'present')}
                                                                        className={classNames('px-2.5 py-1 rounded-lg text-xs font-extrabold shadow-xs transition-colors flex items-center gap-1 cursor-pointer', {
                                                                            'bg-emerald-600 text-white': cls.cram_session_status === 'held',
                                                                            'bg-emerald-50 text-emerald-800 border border-emerald-300 hover:bg-emerald-100': cls.cram_session_status !== 'held'
                                                                        })}
                                                                        title="Record cram school class as held"
                                                                    >
                                                                        {cls.cram_session_status === 'held' ? `✓ ${t('att_status_held')}` : t('att_mark_held')}
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleCramQuickRecord(cls.id, day.date, 'absent')}
                                                                        className={classNames('px-2 py-1 rounded-lg text-xs font-bold transition-colors cursor-pointer flex items-center gap-1', {
                                                                            'bg-red-600 text-white': cls.cram_session_status === 'not_held',
                                                                            'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:bg-red-50': cls.cram_session_status !== 'not_held'
                                                                        })}
                                                                        title="Mark as not held"
                                                                    >
                                                                        {t('att_mark_not_held')}
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleSaveSingleClass(cls.id, day.date, cls.students)}
                                                                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-3 py-1 rounded-lg text-xs shadow-xs transition-colors cursor-pointer"
                                                                >
                                                                    {t('att_save_attendance')}
                                                                </button>
                                                            )}
                                                        </div>

                                                        {/* Table */}
                                                        <div className="p-3">
                                                            <AttendanceTable
                                                                attendanceData={cls.students}
                                                                classInfo={cls}
                                                                currentDate={day.date}
                                                                onStatusChange={(studentId, field, val) => handleStudentChangeInWeek(day.date, cls.id, studentId, field, val)}
                                                                highlightedStudent={highlightedStudent}
                                                                onConfirmPayment={(ids, amt, name, method) => handleConfirmPayment(ids, amt, name, method, cls.id, day.date)}
                                                                onScheduleMakeup={() => fetchWeekData(currentWeekMonday)}
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                        </div>
                    )}
                </div>
            )}

            {/* ========================================================= */}
            {/* VIEW 3: SINGLE CLASS VIEW (BY CLASS)                      */}
            {/* ========================================================= */}
            {viewMode === 'class' && (
                <div className="space-y-5">
                    {/* Class and Date Selectors */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 items-end bg-gray-50/90 p-3 sm:p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                        <div>
                            <label htmlFor="class-select" className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1">
                                Select a Class
                            </label>
                            <select
                                id="class-select"
                                value={selectedClassId}
                                onChange={e => setSelectedClassId(e.target.value)}
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 text-sm bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 font-medium"
                            >
                                <option value="">-- Please select a class --</option>
                                {allClasses.map(cls => (
                                    <option key={cls.id} value={cls.id}>
                                        {cls.name} ({cls.day} at {cls.start_time?.slice(0, 5)}) {cls.class_type === 'cram_school' ? '🏫' : '🏠'}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label htmlFor="date-select" className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1">
                                Select Date
                            </label>
                            <input
                                type="date"
                                id="date-select"
                                value={selectedDate}
                                onChange={e => setSelectedDate(e.target.value)}
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2 text-sm bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 font-medium"
                            />
                        </div>

                        <div>
                            <button
                                onClick={() => setShowOnlyTrials(!showOnlyTrials)}
                                className={classNames(
                                    'w-full py-2.5 px-3 rounded-lg text-xs font-bold transition-all border flex items-center justify-center gap-1.5 cursor-pointer',
                                    showOnlyTrials
                                        ? 'bg-blue-600 text-white border-blue-600 shadow-xs'
                                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100'
                                )}
                            >
                                <span>{showOnlyTrials ? '✓ Showing Trials Only' : 'Show Trial Students Only'}</span>
                            </button>
                        </div>
                    </div>

                    {/* Attendance Table */}
                    {selectedClassId ? (
                        isLoadingClass ? <div className="py-12 text-center"><Spinner /></div> : (
                            <>
                                <AttendanceTable
                                    attendanceData={showOnlyTrials ? singleClassData.filter(s => s.is_trial) : singleClassData}
                                    classInfo={allClasses.find(c => String(c.id) === String(selectedClassId))}
                                    currentDate={selectedDate}
                                    onStatusChange={handleStudentChangeInSingleClass}
                                    highlightedStudent={highlightedStudent}
                                    onConfirmPayment={(ids, amt, name, method) => handleConfirmPayment(ids, amt, name, method, selectedClassId, selectedDate)}
                                    onScheduleMakeup={() => fetchSingleClassData(selectedClassId, selectedDate)}
                                />

                                <div className="mt-6 flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                                    <button 
                                        type="button"
                                        onClick={() => handleSaveSingleClass(selectedClassId, selectedDate, singleClassData)}
                                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-xl text-sm font-bold shadow-sm transition-colors text-center cursor-pointer flex items-center justify-center gap-2"
                                    >
                                        <span>💾</span>
                                        <span>Save Attendance</span>
                                    </button>
                                    <button 
                                        type="button"
                                        onClick={() => handleCancelSession(selectedClassId, selectedDate)} 
                                        className="bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 px-4 py-2.5 rounded-xl text-xs font-semibold transition-colors text-center cursor-pointer"
                                    >
                                        Cancel Class Session
                                    </button>
                                </div>
                            </>
                        )
                    ) : (
                        <div className="text-center py-12 bg-gray-50/50 rounded-xl border border-dashed border-gray-200 dark:border-gray-700">
                            <p className="text-3xl mb-2">📋</p>
                            <p className="text-gray-600 dark:text-gray-400 font-medium text-sm">Please select a class to view enrolled students.</p>
                        </div>
                    )}
                </div>
            )}

            {/* Trial Student Modal */}
            {studentForTrialModal && (
                <TrialStudentModal
                    student={studentForTrialModal}
                    classId={selectedClassId || studentForTrialModal.class_id}
                    onClose={() => setStudentForTrialModal(null)}
                    onUpdate={() => {
                        if (viewMode === 'day') fetchDayData(selectedDate);
                        else if (viewMode === 'week') fetchWeekData(currentWeekMonday);
                        else fetchSingleClassData();
                    }}
                />
            )}
        </div>
    );
}